import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose } from "@mui/icons-material";
// PropertyEnrichmentPanel (PKT-0246) — the read-only "Overview" that finally
// surfaces the enrichment we already own: sectioned amenities / finishes /
// location intel / building / parking (from the attributes JSONB), a map, and
// the ready-made scraped fields (description, rating/reviews, office hours)
// that the whitelist used to drop.
//
// Pure render off data already on the /enriched wire. Fully defensive: every
// section is omitted when it has no signal, and the attribute logic type-guards
// + value-domain-maps in lib/propertyAttributes (never a false "No X", never a
// crash on a missing/list-typed attributes blob).

import React, { useState } from "react";
import { factProvenance, googleReviewsUrl } from "../../lib/propertyProvenance";
import {
  Box,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Link,
  Chip,
  Divider,
  Paper,
  Rating,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Place as PlaceIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import ResultsMap, { MapPoint } from "../ResultsMap";
import {
  getStructuredAttributes,
  countAttributeSignals,
  AttributeSection,
  AttributeSignal,
} from "../../lib/propertyAttributes";

export interface EnrichmentProperty {
  id: string;
  name: string;
  address?: string | null;
  neighborhood?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  attributes?: Record<string, unknown> | null;
  description?: string | null;
  rating?: number | null;
  review_count?: number | null;
  office_hours?: string | null;
  attributes_source?: string | null;
  attributes_updated_at?: string | null;
}

const SectionChips: React.FC<{ section: AttributeSection; property: EnrichmentProperty }> = ({ section, property }) => {
  const [selected,setSelected]=useState<AttributeSignal|null>(null);
  const source=selected ? factProvenance(property,selected.key) : null;
  const theme = useTheme();
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          display: "block",
          mb: 0.75,
        }}
      >
        {section.title}
      </Typography>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
        {section.signals.map((sig: AttributeSignal) => {
          const chip = (
            <Chip
              key={sig.key}
              onClick={() => setSelected(sig)}
              size="small"
              label={
                sig.scope === "select" ? `${sig.label} · select units` : sig.label
              }
              variant="outlined"
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 600,
                borderColor: theme.palette.divider,
                bgcolor: theme.palette.action.hover,
              }}
            />
          );
          return sig.evidence ? (
            <Tooltip key={sig.key} title={sig.evidence} arrow>
              {chip}
            </Tooltip>
          ) : (
            chip
          );
        })}
      </Stack>
      <Dialog open={Boolean(selected)} onClose={()=>setSelected(null)}>
        <DialogTitle sx={{ pr: 7, position: "relative" }}>{selected?.label} · Source<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={()=>setSelected(null)}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
        <DialogContent>
          {source?.quote && <Typography component="blockquote">“{source.quote}”</Typography>}
          {source?.url ? <Link href={source.url} target="_blank" rel="noopener noreferrer">Open captured source</Link> : <Typography>Source unknown — no page URL was retained for this fact.</Typography>}
          <Typography variant="caption" display="block">{source?.recordedAt ? `Enrichment recorded ${new Date(source.recordedAt).toLocaleDateString()}` : 'Enrichment date unknown'} · page capture date unknown</Typography>
          <Typography variant="caption" display="block">No saved page is linked to this fact.</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const PropertyEnrichmentPanel: React.FC<{ property: EnrichmentProperty }> = ({
  property,
}) => {
  const sections = getStructuredAttributes(property.attributes);
  const signalCount = countAttributeSignals(property.attributes);

  const hasCoords =
    typeof property.latitude === "number" &&
    typeof property.longitude === "number" &&
    !Number.isNaN(property.latitude) &&
    !Number.isNaN(property.longitude);

  const mapPoints: MapPoint[] = hasCoords
    ? [
        {
          id: property.id,
          name: property.name,
          lat: property.latitude as number,
          lng: property.longitude as number,
        },
      ]
    : [];

  const description = property.description?.trim();
  const hasRating =
    typeof property.rating === "number" && (property.rating as number) > 0;
  const officeHours = property.office_hours?.trim();

  const nothingToShow =
    signalCount === 0 &&
    !hasCoords &&
    !description &&
    !hasRating &&
    !officeHours;

  if (nothingToShow) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <InfoIcon sx={{ fontSize: 32, color: "text.disabled", mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          No enriched details on file for this property yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Blurb + credibility */}
      {(description || hasRating) && (
        <Box>
          {hasRating && (
            <Stack component="a" href={googleReviewsUrl(property)} target="_blank" rel="noopener noreferrer" aria-label="Find this building’s Google reviews" direction="row" spacing={1} alignItems="center" sx={{ mb: description ? 1 : 0, color: "inherit", textDecoration: "none" }}>
              <Rating
                value={property.rating as number}
                precision={0.1}
                readOnly
                size="small"
                emptyIcon={<StarIcon fontSize="inherit" style={{ opacity: 0.4 }} />}
              />
              <Typography variant="body2" fontWeight={700}>
                {(property.rating as number).toFixed(1)}
              </Typography>
              {typeof property.review_count === "number" && property.review_count > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ({property.review_count.toLocaleString()} recorded reviews · source/date unknown)
                </Typography>
              )}
            </Stack>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {description}
            </Typography>
          )}
        </Box>
      )}

      <Box><Typography variant="h6">Reviews</Typography><Link href={googleReviewsUrl(property)} target="_blank" rel="noopener noreferrer">Find this building on Google Maps · open its reviews</Link><Typography variant="caption" display="block">Google listing search uses name and address. The stored rating's source and read date are not recorded.</Typography></Box>
      {/* Map */}
      {hasCoords && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              display: "block",
              mb: 0.75,
            }}
          >
            Location
          </Typography>
          {(property.address || property.neighborhood) && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <PlaceIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {[property.address, property.neighborhood].filter(Boolean).join(" · ")}
              </Typography>
            </Stack>
          )}
          <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
            <ResultsMap points={mapPoints} height={260} />
          </Box>
        </Box>
      )}

      {/* Enrichment sections */}
      {sections.length > 0 && (
        <Stack spacing={2}>
          {sections.map((section) => (
            <SectionChips key={section.key} section={section} property={property} />
          ))}
        </Stack>
      )}

      {/* Office hours */}
      {officeHours && (
        <>
          <Divider />
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <ScheduleIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.2 }} />
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Office hours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {officeHours}
              </Typography>
            </Box>
          </Stack>
        </>
      )}

      <Paper
        variant="outlined"
        sx={{ p: 1, borderStyle: "dashed", bgcolor: "transparent" }}
      >
        <Typography variant="caption" color="text.secondary">
          {signalCount > 0
            ? `${signalCount} enriched signals shown. Select a chip for its source quote and provenance.`
            : "Location + listing details shown from our enrichment set."}
        </Typography>
      </Paper>
    </Stack>
  );
};

export default PropertyEnrichmentPanel;
